import React, { useEffect, useState } from 'react';
import './About.css';

export default function About() {
  const [clock, setClock] = useState(new Date());

  /* Live clock for editorial rail (matches Courses) */
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const companyStats = [
    { label: 'Core Focus', value: 'Dev & Cloud' },
    { label: 'Workshops', value: '50+' },
    { label: 'Learners', value: '500+' },
    { label: 'Uptime', value: '99.9%' },
  ];

  const coreValues = [
    {
      num: '01',
      title: 'Engineering Excellence',
      description:
        'We adhere to clean architecture, modern tech stacks (MERN, Next.js, React Native), and robust cloud hosting standards for every product we build.',
    },
    {
      num: '02',
      title: 'Knowledge Sharing',
      description:
        'Beyond building software, we conduct hands-on AI workshops, technical webinars, and educational bootcamps to empower the tech community.',
    },
    {
      num: '03',
      title: 'Client-Centric Growth',
      description:
        'We treat every client solution as a strategic partnership, providing continuous support, deployment optimization, and scalability.',
    },
  ];

  const leadershipFocus = [
    {
      title: 'Software Development & Cloud Hosting',
      desc: 'Building high-performance web and mobile applications, setting up CI/CD pipelines, and providing managed cloud infrastructure.',
      highlights: ['MERN & Next.js', 'AWS / Vercel', 'Database Architecture'],
    },
    {
      title: 'Educational Programs & AI Workshops',
      desc: 'Demystifying cutting-edge technologies like Artificial Intelligence, Machine Learning, and Modern Web Development through interactive webinars.',
      highlights: ['Practical AI', 'Live Webinars', 'Upskilling'],
    },
  ];

  return (
    <div className="about-page-root">
      {/* Paper grain overlay */}
      <div className="about-grain-overlay" aria-hidden="true" />

      {/* Editorial side rails */}
      <div className="about-rail about-rail--left">
        <span className="about-rail-text">AVTECH · EST. 2024</span>
        <span className="about-rail-line" />
        <span className="about-rail-text">
          {clock.toLocaleTimeString('en-US', { hour12: false })}
        </span>
      </div>
      <div className="about-rail about-rail--right">
        <span className="about-rail-text">BARAMATI · IN</span>
        <span className="about-rail-line" />
        <span className="about-rail-text">↓</span>
      </div>

      {/* Giant background type */}
      <div className="about-bg-giant-type" aria-hidden="true">
        <span>ABOUT</span>
      </div>

      {/* ==============================
          HERO
         ============================== */}
      <section className="about-hero-section">
        <div className="about-section-inner about-hero-inner">
          <div className="about-hero-badge">
          
            <span className="about-tag"> Who We Are</span>
          </div>

          <h1 className="about-hero-title">
            AV Tech Baramati Pvt. Ltd
            <br />
            <span className="about-hero-title-muted">
              Innovating & <em>educating</em> for the future.
            </span>
          </h1>

          <p className="about-hero-sub">
            A technology company primarily focused on robust full-stack software
            development and seamless cloud hosting — while empowering developers
            through AI workshops and technical webinars.
          </p>

          <div className="about-hero-actions">
            <a href="/contact" className="about-cta-primary">
              <span>Get in touch</span>
              <svg className="about-btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href="/services" className="about-cta-ghost">
              Explore services <span>→</span>
            </a>
          </div>
        </div>
      </section>

      {/* ==============================
          STATS
         ============================== */}
      <section className="about-stats-section">
        <div className="about-section-inner">
          <div className="about-stats-row">
            {companyStats.map((stat, idx) => (
              <div key={idx} className="about-stat-card">
                <span className="about-stat-value">{stat.value}</span>
                <span className="about-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==============================
          MISSION
         ============================== */}
      <section className="about-mission-section">
        <div className="about-section-inner">
          <div className="about-mission-grid">
            <div className="about-mission-text">
              <span className="about-tag">// 02 · Mission</span>
              <h2 className="about-mission-title">
                Building reliable systems,<br />
                <em>inspiring</em> tech innovators.
              </h2>
              <div className="about-mission-divider" />
              <p>
                At <strong>AV Tech Private Limited</strong>, our primary engineering
                engine revolves around building custom software, designing clean
                frontend/backend systems, and deploying scalable web applications.
              </p>
              <p>
                We believe that modern technology should not only be built
                efficiently but also demystified. Through our educational wing,
                we host interactive AI workshops, developer bootcamps, and
                technical webinars to bridge the gap between emerging technology
                and real-world implementation.
              </p>
            </div>

            <div className="about-mission-highlights">
              {leadershipFocus.map((item, index) => (
                <div key={index} className="about-focus-card">
                  <div className="about-focus-num">0{index + 1}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                  <ul className="about-focus-tags">
                    {item.highlights.map((h, i) => (
                      <li key={i} className="about-focus-tag-item">
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          VALUES
         ============================== */}
      <section className="about-values-section">
        <div className="about-section-inner">
          <div className="about-section-header">
            <span className="about-tag">// 03 · Pillars</span>
            <h2 className="about-section-title">
              Driven by values that <em>scale.</em>
            </h2>
            <p className="about-section-sub">
              Principles that guide every product we ship and every mind we mentor.
            </p>
          </div>

          <div className="about-values-grid">
            {coreValues.map((val) => (
              <article key={val.num} className="about-value-card">
                <span className="about-value-num">{val.num}</span>
                <h3 className="about-value-title">{val.title}</h3>
                <p className="about-value-desc">{val.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ==============================
          CTA — Dark editorial section
         ============================== */}
      <section className="about-cta-section">
        <div className="about-bg-giant-type about-bg-giant-type--dark" aria-hidden="true">
          <span>संकल्प</span>
        </div>

        <div className="about-section-inner">
          <div className="about-cta-content">
            <span className="about-cta-tag">// 04 · The Call</span>

            <h2 className="about-cta-title">
              Ready to build or <em>learn</em> with us?
            </h2>

            <div className="about-cta-divider" />

            <p className="about-cta-sub">
              Whether you need scalable cloud application development or want to
              conduct an AI workshop for your team — we're here to help.
            </p>

            <a href="/contact" className="about-cta-primary about-cta-primary--on-dark">
              <span>Get in touch</span>
              <svg className="about-btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}